/*
 * PDP color sibling row: shows prev/next arrows only when the swatches
 * overflow, and hides each arrow at its end of the row. Arrows only scroll
 * the track (CSS scroll-snap settles it on a swatch) — they never change
 * the selected sibling.
 */
if (!customElements.get('sibling-scroller')) {
  class SiblingScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-sibling-scroller-track]');
      this.prev = this.querySelector('[data-sibling-scroller-prev]');
      this.next = this.querySelector('[data-sibling-scroller-next]');
      if (!this.track) return;

      this.prev?.addEventListener('click', this.#onPrev);
      this.next?.addEventListener('click', this.#onNext);
      this.track.addEventListener('scroll', this.#update, {passive: true});

      this.resizeObserver = new ResizeObserver(this.#update);
      this.resizeObserver.observe(this.track);

      this.#revealCurrent();
      this.#update();
    }

    disconnectedCallback() {
      this.prev?.removeEventListener('click', this.#onPrev);
      this.next?.removeEventListener('click', this.#onNext);
      this.track?.removeEventListener('scroll', this.#update);
      this.resizeObserver?.disconnect();
    }

    #onPrev = () => this.#scrollByPage(-1);
    #onNext = () => this.#scrollByPage(1);

    // Move by most of the visible width so one swatch stays in view for context.
    #scrollByPage(direction) {
      const swatch = this.track.querySelector('.swatch__button');
      const step = Math.max(this.track.clientWidth - (swatch?.offsetWidth || 0), swatch?.offsetWidth || 0);
      this.track.scrollBy({left: direction * step, behavior: 'smooth'});
    }

    // On load, bring the viewed color into the row if it sits past the visible
    // edge. Only the track scrolls (not scrollIntoView, which also moves the page).
    #revealCurrent() {
      const current = this.track.querySelector('.sibling__link--current')?.closest('.swatch__button');
      if (!current) return;

      const trackRect = this.track.getBoundingClientRect();
      const currentRect = current.getBoundingClientRect();
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const visibleStart = trackRect.left + padding;
      const visibleEnd = trackRect.right - padding;
      if (currentRect.left >= visibleStart && currentRect.right <= visibleEnd) return;

      this.track.scrollLeft += currentRect.left - visibleStart;
    }

    #update = () => {
      const {scrollLeft, scrollWidth, clientWidth} = this.track;
      const maxScroll = scrollWidth - clientWidth;
      const overflows = maxScroll > 1;

      this.toggleAttribute('data-overflow', overflows);
      this.toggleAttribute('data-at-start', !overflows || scrollLeft <= 1);
      this.toggleAttribute('data-at-end', !overflows || scrollLeft >= maxScroll - 1);
    };
  }

  customElements.define('sibling-scroller', SiblingScroller);
}
